import React from 'react'
import PropTypes from 'prop-types'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {Box, Flex, Heading, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import DisplayPrice from '../../display-price'
import ProductLikes from '../product-likes'
import ProductCallout from './product-callout'
import ProductTileDetailsColorSwatches from './product-tile-details-color-swatches'
import SingleColorSwatch from './single-color-swatch'
import {getTileSwatchData, hasTileColorSwatches} from '../utils/product-tile-utils'

const ProductTileDetails = (props) => {
    const {
        styles,
        localizedProductName,
        isShopTheLook,
        /* Rows the details band must reserve below title + price (badge row,
         * swatch row) — see `reservedDetailRows` in the product tile. Two rows
         * take the taller min-height. */
        reservedDetailRows,
        detailsWrapperOverride,
        showSwatches,
        product,
        isWishlistEnabled,
        showProductLikes,
        isProductInWishlist,
        wishlistCount,
        isHovered,
        handleFavouriteClick,
        isLoadingFreshLikes,
        isWishlistPage,
        productWithFilteredVariants,
        priceData,
        currency,
        productPromotionObject,
        isOOS,
        intl,
        colorValue,
        setColorValue,
        setProductId,
        productId,
        isGiftCard,
        responsiveDisplay,
        isProductSet,
        isNearViewport
    } = props
    const showPromoSwatches = !isHovered
    const hasPromoName = Boolean(productPromotionObject?.name)

    return (
        <Box
            {...styles.detailsWrapper}
            {...(reservedDetailRows > 1 ? styles.detailsWrapperTall : null)}
            {...(detailsWrapperOverride || {})}
        >
            {/* Title */}
            <Flex {...styles.bodyText} {...styles.titleRow}>
                {isShopTheLook ? (
                    <Text
                        as="h3"
                        variant="plpH3"
                        {...styles.title}
                        data-testid="product-name"
                        sx={responsiveDisplay?.productName}
                    >
                        {localizedProductName}
                    </Text>
                ) : (
                    <Text
                        as="h2"
                        {...styles.title}
                        data-testid="product-name"
                        sx={responsiveDisplay?.productName}
                    >
                        {localizedProductName}
                    </Text>
                )}

                {/* Single color swatch on Mobile */}
                {!isGiftCard && (
                    <>
                        <HideOnDesktop marginLeft="auto">
                            {showSwatches && (
                                <Box sx={responsiveDisplay?.colorSwatches}>
                                    <SingleColorSwatch
                                        swatchData={getTileSwatchData(product)}
                                        styles={styles}
                                    />
                                </Box>
                            )}
                        </HideOnDesktop>

                        {/* Product Likes */}
                        {isWishlistEnabled && showProductLikes && (
                            <Box sx={responsiveDisplay?.wishlistIcon}>
                                <Flex {...styles.productLikesWrapper}>
                                    <ProductLikes
                                        isFavourite={isProductInWishlist}
                                        wishlistCount={wishlistCount}
                                        isHovered={isHovered}
                                        onFavouriteToggle={handleFavouriteClick}
                                        productName={localizedProductName}
                                        isLoadingFreshLikes={isLoadingFreshLikes}
                                        wishlistProductId={productId || product?.id}
                                    />
                                </Flex>
                            </Box>
                        )}
                    </>
                )}
            </Flex>

            {!isGiftCard && isWishlistPage && productWithFilteredVariants?.variationValues && (
                <Text {...styles.wishlistVariantText}>
                    {productWithFilteredVariants.variationValues.size && (
                        <>
                            {intl.formatMessage({
                                id: 'product_tile.label.size',
                                defaultMessage: 'Size'
                            })}
                            : {productWithFilteredVariants.variationValues.size}{' '}
                        </>
                    )}
                    {productWithFilteredVariants.variationValues.length && (
                        <>
                            {intl.formatMessage({
                                id: 'product_tile.label.length',
                                defaultMessage: 'Length'
                            })}
                            : {productWithFilteredVariants.variationValues.length}
                        </>
                    )}
                </Text>
            )}

            {!isGiftCard && !isProductSet && (
                <Box sx={responsiveDisplay?.productPrice}>
                    {/* Price updates reactively from priceData — no skeleton. */}
                    <>
                            {/* Price */}
                            <Flex {...styles.bodyText} {...styles.priceRow}>
                                <DisplayPrice
                                    priceData={priceData}
                                    currency={currency}
                                    currentPriceProps={{
                                        fontWeight: 'medium'
                                    }}
                                    listPriceProps={{
                                        fontWeight: 'medium'
                                    }}
                                />
                                {isWishlistPage && isOOS && (
                                    <Text {...styles.oosText}>
                                        {intl.formatMessage({
                                            id: 'product_tile.label.oos',
                                            defaultMessage: 'Sold Out'
                                        })}
                                    </Text>
                                )}
                            </Flex>
                            {hasPromoName && (
                                <Text
                                    {...styles.bodyText}
                                    {...styles.promotionText}
                                    color={
                                        priceData.isOnSale
                                            ? 'webTypo.textPrimary'
                                            : 'webTypo.textSale'
                                    }
                                    className="product-promotion"
                                >
                                    {productPromotionObject.name}
                                </Text>
                            )}
                    </>
                </Box>
            )}

            {!isGiftCard && (
                <Box display="flex" flexDirection="column">
                    {/* Product promotions */}
                    <Box
                        data-testid="product-callout-slot"
                        order={showPromoSwatches ? 1 : 2}
                        sx={responsiveDisplay?.productTags}
                    >
                        <ProductCallout
                            product={product}
                            styles={styles}
                            intl={intl}
                            props={{showPromoSwatches}}
                        />
                    </Box>

                    {/* Product tile color swatches — mounted in SSR, revealed on hover. Out of flow
                     * while hidden so the callout below isn't pushed down. */}
                    <HideOnMobile position="relative">
                        <Box
                            data-testid="product-swatches-slot"
                            order={1}
                            position={isHovered ? 'static' : 'absolute'}
                            sx={responsiveDisplay?.colorSwatches}
                            visibility={isHovered ? 'visible' : 'hidden'}
                            opacity={isHovered ? 1 : 0}
                        >
                            {(isHovered || hasTileColorSwatches(product)) && !isProductSet && (
                                <ProductTileDetailsColorSwatches
                                    product={product}
                                    styles={styles}
                                    colorValue={colorValue}
                                    setColorValue={setColorValue}
                                    setProductId={setProductId}
                                    productId={productId}
                                    isNearViewport={isNearViewport}
                                />
                            )}
                        </Box>
                    </HideOnMobile>
                </Box>
            )}
        </Box>
    )
}

ProductTileDetails.propTypes = {
    styles: PropTypes.object,
    localizedProductName: PropTypes.string,
    isShopTheLook: PropTypes.bool,
    reservedDetailRows: PropTypes.number,
    detailsWrapperOverride: PropTypes.object,
    showSwatches: PropTypes.bool,
    product: PropTypes.object,
    isWishlistEnabled: PropTypes.bool,
    showProductLikes: PropTypes.bool,
    isProductInWishlist: PropTypes.bool,
    wishlistCount: PropTypes.number,
    isHovered: PropTypes.bool,
    isNearViewport: PropTypes.bool,
    handleFavouriteClick: PropTypes.func,
    isLoadingFreshLikes: PropTypes.bool,
    isWishlistPage: PropTypes.bool,
    productWithFilteredVariants: PropTypes.object,
    priceData: PropTypes.object,
    currency: PropTypes.string,
    productPromotionObject: PropTypes.object,
    isOOS: PropTypes.bool,
    intl: PropTypes.object,
    colorValue: PropTypes.string,
    setColorValue: PropTypes.func,
    setProductId: PropTypes.func,
    productId: PropTypes.string,
    isGiftCard: PropTypes.bool,
    responsiveDisplay: PropTypes.object
}

export default ProductTileDetails
